import React, { useState, useEffect } from 'react';
import { updateOrderStatus, getOrderStats } from '../services/api';

const STATUS_CONFIG = {
  ORDER_RECEIVED: { label: 'Order Received', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  FILE_GENERATED: { label: 'File Generated', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  ORDER_TRADED:   { label: 'Order Traded',   bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
};

const EXCHANGE_STYLE = {
  NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', btnBg: '#1d4ed8' },
  BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', btnBg: '#15803d' },
  MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', btnBg: '#c2410c' },
};

const DEFAULT_SLICE = {
  NIFTY: 1755, BANKNIFTY: 600, FINNIFTY: 1800,
  MIDCPNIFTY: 2800, SENSEX: 500, BANKEX: 600
};

const TODAY = new Date().toISOString().slice(0, 10);

function fmtDT(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function SqOffOrders() {
  const [orders,      setOrders]      = useState([]);
  const [stats,       setStats]       = useState({});
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState([]);
  const [filters,     setFilters]     = useState({ exchange: 'ALL', segment: 'ALL', status: 'ALL', date_from: TODAY, date_to: TODAY });
  const [toast,       setToast]       = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sliceQty,    setSliceQty]    = useState(DEFAULT_SLICE);
  const [showSlice,   setShowSlice]   = useState(false);
  const [sliceEdit,   setSliceEdit]   = useState({});
  const [sliceSaving, setSliceSaving] = useState(false);
  const [modal,       setModal]       = useState(null); // { type, orders }

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };
  const token = () => localStorage.getItem('nb_admin_token');
  const authH = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.exchange !== 'ALL') params.append('exchange',  filters.exchange);
      if (filters.segment  !== 'ALL') params.append('segment',   filters.segment);
      if (filters.status   !== 'ALL') params.append('status',    filters.status);
      if (filters.date_from)          params.append('date_from', filters.date_from);
      if (filters.date_to)            params.append('date_to',   filters.date_to);
      const [ordersRes, statsRes] = await Promise.all([
        fetch('/api/admin/orders?' + params.toString(), { headers: authH() }).then(r => r.json()),
        fetch('/api/admin/orders/stats',                { headers: authH() }).then(r => r.json()),
      ]);
      setOrders(ordersRes.orders || []);
      setStats(statsRes.stats   || {});
      setSelected([]);
    } catch { showToast('Failed to load orders.', 'error'); }
    finally { setLoading(false); }
  };

  const loadSliceQty = async () => {
    try {
      const res  = await fetch('/api/admin/slice-qty', { headers: authH() });
      const data = await res.json();
      if (data.sliceQty) {
        const map = { ...DEFAULT_SLICE };
        data.sliceQty.forEach(r => { map[r.symbol] = r.slice_qty; });
        setSliceQty(map); setSliceEdit(map);
      }
    } catch {}
  };

  useEffect(() => { loadOrders(); loadSliceQty(); }, []);

  const saveSliceQty = async () => {
    setSliceSaving(true);
    try {
      await Promise.all(Object.entries(sliceEdit).map(([symbol, qty]) =>
        fetch(`/api/admin/slice-qty/${symbol}`, { method: 'PUT', headers: authH(), body: JSON.stringify({ slice_qty: qty }) })
      ));
      setSliceQty({ ...sliceEdit });
      showToast('Slice quantities updated.');
      setShowSlice(false);
    } catch { showToast('Failed to save.', 'error'); }
    finally { setSliceSaving(false); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll    = ()    => setSelected(selected.length === orders.length ? [] : orders.map(o => o.order_id));

  const getSelectedExchange = () => {
    if (!selected.length) return null;
    const sel = orders.filter(o => selected.includes(o.order_id));
    const exs = [...new Set(sel.map(o => o.exchange))];
    return exs.length === 1 ? exs[0] : 'MIXED';
  };

  // ── File helpers ──────────────────────────────────────────────────────────
  const formatFileDate = (d) => {
    const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return String(d.getDate()).padStart(2,'0') + M[d.getMonth()] + d.getFullYear();
  };
  const formatFileTime  = (d) => [d.getHours(),d.getMinutes(),d.getSeconds()].map(n=>String(n).padStart(2,'0')).join('-');
  const formatNEATExpiry = (ds) => {
    if (!ds) return '';
    const d = new Date(ds);
    const M = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    return String(d.getDate()).padStart(2,'0') + M[d.getMonth()] + d.getFullYear();
  };
  const pad = (val, len) => String(val || '').padEnd(len, ' ');
  const extractBase = (sym) => {
    for (const idx of ['MIDCPNIFTY','BANKNIFTY','FINNIFTY','NIFTY','SENSEX','BANKEX'])
      if ((sym||'').toUpperCase().startsWith(idx)) return idx;
    return ((sym||'').match(/^([A-Z]+)/)||['',''])[1];
  };
  const extractCMSym = (s) => {
    if (!s) return '';
    if (s.includes(';')) return s.split(';')[0].replace(/ EQ$|BE$/, '').trim();
    return s.trim();
  };
  const splitSlices = (qty, sliceQ) => {
    if (!sliceQ || qty <= sliceQ) return [qty];
    const rows = []; let rem = qty;
    while (rem > 0) { rows.push(Math.min(rem, sliceQ)); rem -= Math.min(rem, sliceQ); }
    return rows;
  };
  const getNEATInstr = (sym, opt) => {
    const base = extractBase(sym).toUpperCase();
    const isIdx = ['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX','BANKEX'].includes(base);
    return opt ? (isIdx ? 'OPTIDX' : 'OPTSTK') : (isIdx ? 'FUTIDX' : 'FUTSTK');
  };
  const buildCMRow = (o, qty) => [
    pad('1',13), pad(o.side==='BUY'?'1':'2',2), pad('2',2),
    pad(extractCMSym(o.symbol),10), pad('EQ',5), pad('1',2),
    pad('',11), pad('',2), pad('',9), pad('MKT',10), pad('',8),
    pad(qty,9), pad('',9), pad('07708',12), pad('2',2),
    pad(o.ucc,10), pad('',24), pad('',16), pad('',10)
  ].join(',');
  const buildFORow = (o, qty) => [
    pad('1',13), pad(o.option_type?'O':'F',1), pad('U',1),
    pad(o.side==='BUY'?'1':'2',2), pad('0',2), pad('2',2),
    pad(getNEATInstr(o.symbol,o.option_type),8), pad(extractBase(o.symbol),10),
    pad(formatNEATExpiry(o.expiry_date),9), pad(o.strike_price?String(Math.round(o.strike_price)):'',10),
    pad(o.option_type||'',2), pad('1',2), pad('',11), pad('',2), pad('',9),
    pad('MKT',10), pad('',10), pad(qty,5), pad('',9),
    pad('07708',12), pad('2',2), pad(o.ucc,10), pad('',24), pad('0',2),
    pad('',16), pad('',12)
  ].join(',');

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!selected.length) return showToast('Select at least one order.', 'error');
    const selEx = getSelectedExchange();
    if (selEx === 'MIXED') return showToast('Select orders from one exchange only.', 'error');
    setDownloading(true);
    try {
      const selOrders = orders.filter(o => selected.includes(o.order_id));
      const now = new Date();
      const fd  = formatFileDate(now);
      const ft  = formatFileTime(now);
      const nseCM = selOrders.filter(o => o.exchange === 'NSE' && o.segment === 'CM');
      const nseFO = selOrders.filter(o => o.exchange === 'NSE' && o.segment === 'FO');
      const bseCM = selOrders.filter(o => o.exchange === 'BSE' && o.segment === 'CM');
      const bseFO = selOrders.filter(o => o.exchange === 'BSE' && o.segment === 'FO');
      const files = [];

      if (nseCM.length > 0) {
        const lines = [];
        for (const o of nseCM) splitSlices(o.quantity, null).forEach(q => lines.push(buildCMRow(o, q)));
        files.push({ name: `BasketCM${fd}-${ft}.txt`, content: lines.join('\r\n'), type: 'text/plain' });
      }
      if (nseFO.length > 0) {
        const lines = [];
        for (const o of nseFO) {
          const sq = sliceQty[extractBase(o.symbol).toUpperCase()] || null;
          splitSlices(o.quantity, sq).forEach(q => lines.push(buildFORow(o, q)));
        }
        files.push({ name: `BasketFO${fd}-${ft}.txt`, content: lines.join('\r\n'), type: 'text/plain' });
      }
      if (bseCM.length > 0 || bseFO.length > 0) {
        const res  = await fetch('/api/admin/orders/download', {
          method: 'POST', headers: authH(),
          body: JSON.stringify({ order_ids: [...bseCM,...bseFO].map(o=>o.order_id), exchange: 'BSE' })
        });
        const data = await res.json();
        if (data.files) data.files.forEach(f => files.push({
          name: f.filename, type: f.contentType,
          content: Uint8Array.from(atob(f.content), c => c.charCodeAt(0))
        }));
      }

      if (files.length === 0) return showToast('No files generated. Check order segments.', 'error');

      for (const file of files) {
        const blob = new Blob([file.content], { type: file.type });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        await new Promise(r => setTimeout(r, 300));
      }
      await fetch('/api/admin/orders/mark-generated', {
        method: 'POST', headers: authH(),
        body: JSON.stringify({ order_ids: selected })
      });
      showToast(`${files.length} file(s) downloaded for ${selected.length} orders.`);
      loadOrders();
    } catch (err) {
      showToast('Download failed: ' + err.message, 'error');
    } finally { setDownloading(false); }
  };

  const handleMarkTraded = async () => {
    if (!selected.length) return showToast('Select at least one order.', 'error');
    try {
      await Promise.all(selected.map(id => fetch('/api/admin/orders/status', {
        method: 'PUT', headers: authH(),
        body: JSON.stringify({ order_id: id, status: 'ORDER_TRADED' })
      })));
      showToast(`${selected.length} orders marked as traded.`);
      loadOrders();
    } catch { showToast('Update failed.', 'error'); }
  };

  // ── Stat card drill-down data ─────────────────────────────────────────────
  const openModal = (type) => {
    const todayStr = TODAY;
    let filtered = [];
    if (type === 'today')    filtered = orders.filter(o => o.placed_at?.slice(0,10) === todayStr);
    if (type === 'received') filtered = orders.filter(o => o.status === 'ORDER_RECEIVED');
    if (type === 'generated') filtered = orders.filter(o => o.status === 'FILE_GENERATED');
    if (type === 'traded')   filtered = orders.filter(o => o.status === 'ORDER_TRADED');
    setModal({ type, orders: filtered });
  };

  const setFilter  = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const inputStyle = { padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#0f172a', outline: 'none' };
  const selEx      = getSelectedExchange();
  const COLS       = '36px 90px 130px 100px 70px 70px 100px 80px 70px 50px 70px 110px 110px 100px';

  const statCards = [
    { label: "Today's Orders", value: stats.today          || 0, color: '#1d4ed8', bg: '#eff6ff', type: 'today'     },
    { label: 'Order Received', value: stats.received       || 0, color: '#7c3aed', bg: '#fdf4ff', type: 'received'  },
    { label: 'File Generated', value: stats.file_generated || 0, color: '#c2410c', bg: '#fff7ed', type: 'generated' },
    { label: 'Order Traded',   value: stats.traded         || 0, color: '#15803d', bg: '#f0fdf4', type: 'traded'    },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4', border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: toast.type === 'error' ? '#dc2626' : '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', maxWidth: 400 }}>
          {toast.msg}
        </div>
      )}

      {/* Slice Qty Modal */}
      {showSlice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Slice Quantity Configuration</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Update exchange-defined slice quantities for index options</div>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {Object.entries(sliceEdit).map(([sym, qty]) => (
                  <div key={sym} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{sym}</div>
                    <input type="number" value={qty} onChange={e => setSliceEdit(s => ({ ...s, [sym]: parseInt(e.target.value)||0 }))}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, fontWeight: 600, color: '#1d4ed8', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginTop: 14, fontSize: 11, color: '#92400e' }}>
                ⚠ Update only when exchange revises slice quantities.
              </div>
            </div>
            <div style={{ padding: '14px 24px 18px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, height: 38, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#374151', fontSize: 13, cursor: 'pointer' }} onClick={() => { setShowSlice(false); setSliceEdit({...sliceQty}); }}>Cancel</button>
              <button style={{ flex: 2, height: 38, background: '#1d4ed8', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={saveSliceQty} disabled={sliceSaving}>{sliceSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Drill-Down Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 1000, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '18px 24px 14px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                  {modal.type === 'today'     ? "Today's Orders"
                  : modal.type === 'received' ? 'Order Received'
                  : modal.type === 'generated'? 'File Generated'
                  :                             'Order Traded'}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{modal.orders.length} records</div>
              </div>
              <button onClick={() => setModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>✕ Close</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {modal.orders.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No records found.</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 110px 1fr 70px 60px 60px 70px 140px 140px 120px 130px', padding: '10px 20px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0', position: 'sticky', top: 0 }}>
                    {['UCC', 'Client', 'Symbol', 'Exch', 'Seg', 'Side', 'Qty',
                      modal.type === 'generated' ? 'File Gen Time' : 'Placed At',
                      modal.type === 'traded' ? 'Traded At' : 'Status',
                      'Placed By', modal.type === 'generated' ? 'RMS User' : 'Dealer ID'
                    ].map(h => (
                      <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                    ))}
                  </div>
                  {modal.orders.map((o, i) => {
                    const st = STATUS_CONFIG[o.status] || STATUS_CONFIG.ORDER_RECEIVED;
                    return (
                      <div key={o.order_id} style={{ display: 'grid', gridTemplateColumns: '100px 110px 1fr 70px 60px 60px 70px 140px 140px 120px 130px', padding: '11px 20px', alignItems: 'center', borderBottom: i < modal.orders.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{o.ucc}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.client_name || '—'}</div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{o.symbol}</div>
                        <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: EXCHANGE_STYLE[o.exchange]?.bg, color: EXCHANGE_STYLE[o.exchange]?.color, border: `0.5px solid ${EXCHANGE_STYLE[o.exchange]?.border}` }}>{o.exchange}</span></div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.segment}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: o.side === 'SELL' ? '#dc2626' : '#15803d' }}>{o.side}</div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{o.quantity}</div>
                        <div style={{ fontSize: 11, color: '#374151' }}>{fmtDT(modal.type === 'generated' ? o.file_generated_at : o.placed_at)}</div>
                        <div>
                          {modal.type === 'traded'
                            ? <span style={{ fontSize: 11, color: '#15803d', fontWeight: 500 }}>{fmtDT(o.traded_at)}</span>
                            : <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{st.label}</span>
                          }
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.placed_by || 'CLIENT'}</div>
                        <div style={{ fontSize: 11, fontWeight: o.dealer_id ? 600 : 400, color: o.dealer_id ? '#1d4ed8' : '#94a3b8' }}>{o.dealer_id || '—'}</div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <div style={{ padding: '12px 24px', borderTop: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>{modal.orders.length} total records</span>
              <button onClick={() => setModal(null)} style={{ padding: '7px 20px', borderRadius: 7, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards — Clickable */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {statCards.map(c => (
          <div key={c.label}
            onClick={() => openModal(c.type)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = c.color; e.currentTarget.style.boxShadow = `0 0 0 2px ${c.bg}`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s' }}>
            <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color, textDecoration: 'underline', textUnderlineOffset: 3 }}>{c.value}</div>
              <span style={{ fontSize: 10, color: c.color, background: c.bg, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>View →</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: '14px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filters.exchange} onChange={e => setFilter('exchange', e.target.value)} style={inputStyle}>
            {['ALL','NSE','BSE','MCX'].map(e => <option key={e} value={e}>{e === 'ALL' ? 'All Exchanges' : e}</option>)}
          </select>
          <select value={filters.segment}  onChange={e => setFilter('segment',  e.target.value)} style={inputStyle}>
            {['ALL','CM','FO'].map(s => <option key={s} value={s}>{s === 'ALL' ? 'All Segments' : s}</option>)}
          </select>
          <select value={filters.status}   onChange={e => setFilter('status',   e.target.value)} style={inputStyle}>
            <option value="ALL">All Status</option>
            <option value="ORDER_RECEIVED">Order Received</option>
            <option value="FILE_GENERATED">File Generated</option>
            <option value="ORDER_TRADED">Order Traded</option>
          </select>
          <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} style={inputStyle} />
          <input type="date" value={filters.date_to}   onChange={e => setFilter('date_to',   e.target.value)} style={inputStyle} />
          <button onClick={loadOrders} style={{ ...inputStyle, background: '#1d4ed8', color: '#fff', cursor: 'pointer', border: 'none' }}>Apply</button>
          <button onClick={() => { setFilters({ exchange: 'ALL', segment: 'ALL', status: 'ALL', date_from: '', date_to: '' }); setTimeout(loadOrders, 100); }} style={{ ...inputStyle, cursor: 'pointer' }}>Reset</button>
          <button onClick={() => { setSliceEdit({...sliceQty}); setShowSlice(true); }} style={{ ...inputStyle, cursor: 'pointer' }}>⚙ Slice Qty</button>
          <div style={{ flex: 1 }} />
          {selected.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {selected.length} selected
                {selEx === 'MIXED' && <span style={{ color: '#dc2626', marginLeft: 6 }}>— one exchange only</span>}
              </span>
              <button onClick={handleDownload} disabled={downloading || selEx === 'MIXED'}
                style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: selEx === 'MIXED' ? '#f1f5f9' : (EXCHANGE_STYLE[selEx]?.btnBg || '#1d4ed8'), color: selEx === 'MIXED' ? '#94a3b8' : '#fff', fontSize: 12, fontWeight: 600, cursor: downloading || selEx === 'MIXED' ? 'not-allowed' : 'pointer' }}>
                {downloading ? 'Generating...' : `↓ Download ${selEx !== 'MIXED' && selEx ? selEx + ' ' : ''}File`}
              </button>
              <button onClick={handleMarkTraded} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                ✓ Mark Traded
              </button>
            </div>
          )}
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(sliceQty).map(([sym, qty]) => (
            <span key={sym} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569', border: '0.5px solid #e2e8f0' }}>{sym}: {qty.toLocaleString()}</span>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '10px 16px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
          <div><input type="checkbox" checked={selected.length === orders.length && orders.length > 0} onChange={toggleAll} style={{ cursor: 'pointer' }} /></div>
          {['UCC','Client','Symbol','Exch','Seg','Expiry','Strike','Type','B/S','Qty','Placed On','Traded On','Status'].map(h => (
            <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>No sq-off orders found.</div>
        ) : orders.map((o, i) => {
          const st      = STATUS_CONFIG[o.status] || STATUS_CONFIG.ORDER_RECEIVED;
          const isSel   = selected.includes(o.order_id);
          const exStyle = EXCHANGE_STYLE[o.exchange] || {};
          const base    = extractBase(o.symbol);
          const sq      = sliceQty[base?.toUpperCase()];
          const split   = sq ? splitSlices(o.quantity, sq).length : 1;
          return (
            <div key={o.order_id} style={{ display: 'grid', gridTemplateColumns: COLS, padding: '11px 16px', alignItems: 'center', borderBottom: i < orders.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: isSel ? '#f0f7ff' : '#fff' }}>
              <input type="checkbox" checked={isSel} onChange={() => toggleSelect(o.order_id)} style={{ cursor: 'pointer' }} />
              <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{o.ucc}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.client_name || '—'}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>
                {o.symbol}
                {sq && o.quantity > sq && <span style={{ fontSize: 9, background: '#fff7ed', color: '#c2410c', border: '0.5px solid #fed7aa', borderRadius: 3, padding: '1px 4px', marginLeft: 4 }}>→{split} rows</span>}
              </div>
              <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: exStyle.bg, color: exStyle.color, border: `0.5px solid ${exStyle.border}` }}>{o.exchange}</span></div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.segment}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.expiry_date ? new Date(o.expiry_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.strike_price || '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{o.option_type  || '—'}</div>
              <div><span style={{ fontSize: 11, fontWeight: 600, color: o.side === 'SELL' ? '#dc2626' : '#15803d' }}>{o.side}</span></div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{o.quantity}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>{new Date(o.placed_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</div>
              <div style={{ fontSize: 10, color: o.traded_at ? '#15803d' : '#94a3b8' }}>{o.traded_at ? new Date(o.traded_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}</div>
              <div><span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{st.label}</span></div>
            </div>
          );
        })}
      </div>
      {orders.length > 0 && (
        <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
          {orders.length} orders · {selected.length} selected
          {selEx && selEx !== 'MIXED' && <span style={{ marginLeft: 8, color: '#1d4ed8' }}>· All selected: {selEx}</span>}
        </div>
      )}
    </div>
  );
}